import React, {useEffect, useState} from 'react';
import styles from "../styles/SchoolFeedWidget.module.scss";
import listItemStyles from "../ContentPostDisplay/styles/ListItemGroup.module.scss";
import FlatIcon from "../../utilities/FlatIcon";
import {getCdnUrl} from "../../../utils/SchoolBlocksUtilities";
import FontAwesome from "../../utilities/FontAwesome";
import moment, {Moment} from 'moment';
import classNames from 'classnames';
import {observer} from "mobx-react";
import ImageComponent from "../../utilities/ImageComponent";
import {getIconByMimeType} from "../../blocks/TerracedGridTheme/FilesFoldersBlock/fileUtilities";
import {getConfig} from "../../../_configs/AppConfig";
import MarkdownEditor from "../../editors/MarkdownEditor";
import {isEvent, isFile, isMedia, isNews} from "../ContentPostDisplay/utils";
import {LocationEditor} from "./LocationEditor";
import {DateEditor} from "./DateEditor";
import {ContentItemSelect} from "./ContentItemSelect";
import {useGridType} from "../../../hooks/useGridType";
import {GridTypes} from "../../../pages/MainGridPage";
import {ITerraceTypes, terraceTypeToTitleMap} from "../../blocks/TerracedGridTheme/Terrace";
import {values} from "mobx";
import NotificationManager from "../../notifications/NotificationManager";

import {RequiredInput} from "../../blocks/SchoolBlocks/EditBlockComponents/EditBlockInputs/RequiredInput";
import {RequiredDiv} from "../../blocks/SchoolBlocks/EditBlockComponents/EditBlockInputs/RequiredDiv";
import {useMachine} from "@xstate/react";
import {FetchMachine} from "../../machines/FetchMachine";
import WatsonApi from "../../../backends/WatsonApi";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import ToggleSwitch from "../../admin/sidebar/items/ToggleSwitch";
import {
    IReactiveCourseWorkContentItem,
    IReactiveEventContentItem,
    IReactiveMediaContentItem,
    IReactiveNewsContentItem,
    ReactiveContentItem,
} from "../SchoolFeedContentItem";
import {useCreateContentItem} from "../../../hooks/useCreateContentItem";
import useSWR from "swr";
import {UrlEditor} from "./UrlEditor";
import {ImageEditor} from "./ImageEditor";
import {SharePathsEditor} from "./SharePathsEditor";
import {useStores} from "../../../stores/useStores";
import Button from "../../utilities/Button";
import OrgSupportedLocaleSelector from "../../utilities/OrgSupportedLocaleSelector";
import {localeRegistry} from "../../../internationalization/SupportedLocales";
import {
    formatContentItemSharesForBackend,
    IMinimalOrganization,
    initializeSharingOrganizations,
    validateContentItemSharingOrganizations,
} from "../../../utils/organizationSharingUtils";

const submitDateFormat = "YYYY-MM-DDTHH:mm";
export const displayDateFormat = "dddd, MMM D";

const {publicRuntimeConfig} = getConfig();

export function getDefaultTimes() {
    const start = moment();
    const minutes = start.minutes();
    if (minutes % 15 !== 0) {
        start.minutes(Math.ceil(minutes / 15) * 15);
    }
    return {
        start,
        end: moment(start).add(1, "hour"),
    };
}

export const enum SupportedContentItemTypes {
    TOPIC = "topic",
    NEWS = "news",
    MEDIA = "media",
    FILE = "files",
    EVENT = "events",
}

export function getTerraceTypeFromSupportedContentItemType(contentItemType: SupportedContentItemTypes): ITerraceTypes {
    switch (contentItemType) {
        case SupportedContentItemTypes.EVENT:
            return ITerraceTypes.EVENTS
        case SupportedContentItemTypes.FILE:
            return ITerraceTypes.FILES
        case SupportedContentItemTypes.NEWS:
            return ITerraceTypes.NEWS
        case SupportedContentItemTypes.TOPIC:
            return ITerraceTypes.NEWS
        case SupportedContentItemTypes.MEDIA:
            return ITerraceTypes.MEDIA
    }
}

export function typeFromContentItem(contentItem) {
    if (!contentItem) return null;
    else if (contentItem.is_news && !contentItem.meritchat_enabled) return SupportedContentItemTypes.NEWS;
    else if (contentItem.is_news && contentItem.meritchat_enabled) return SupportedContentItemTypes.TOPIC;
    else if (contentItem.is_media) return SupportedContentItemTypes.MEDIA;
    else if (contentItem.is_event) return SupportedContentItemTypes.EVENT;
    else if (contentItem.is_file || contentItem.is_folder) return SupportedContentItemTypes.FILE;
    return null;
}

export const SchoolFeedPostManager = observer((props: {
    contentItem: IReactiveEventContentItem | IReactiveNewsContentItem | IReactiveCourseWorkContentItem | IReactiveMediaContentItem | null,
}) => {
    const [current, send] = useMachine(FetchMachine);
    const {
        organizationStore,
        modalStore,
        i18nStore,
        gridStore,
        sidebarStore,
        interfaceStore,
    } = useStores();

    const gridType = useGridType();
    const {handleSave} = useCreateContentItem();
    const intent: "edit" | "create" = props.contentItem ? "edit" : "create";

    const [contentItemType, setContentItemType] = useState<SupportedContentItemTypes | null>(null);
    const [postToFacebook, setPostToFacebook] = useState<boolean>(false);
    const [meritChatEnabled, setMeritChatEnabled] = useState(organizationStore.schoolFeedEnabledForOrganization);
    const [published, setPublished] = useState(true);
    const [contentItem, setContentItem] = useState<ContentItem | null>(null);

    useAsyncEffect(async () => {
        if (props.contentItem) {
            try {
                const fieldsToExpand = [
                    "organization",
                    "organization.is_editor",
                    "share_organizations",
                    "share_request_organizations",
                ];
                const itemType = typeFromContentItem(props.contentItem);
                if (itemType === SupportedContentItemTypes.TOPIC || itemType === SupportedContentItemTypes.NEWS) {
                    fieldsToExpand.push("discussion_prompt");
                    fieldsToExpand.push("is_prompt_editable");
                }

                send("FETCH");
                const client = await WatsonApi();
                const result = await client.apis.organizations.organizations_content_items_read({
                    organization_pk: props.contentItem.organization_id,
                    id: props.contentItem?.id,
                    expand: fieldsToExpand,
                    raw: true,
                });
                const resultObj = new ReactiveContentItem(result.obj);

                setContentItem(resultObj)
            } catch (e) {
                send("REJECT");
                NotificationManager.error('Content Item not found');
            }
        }
    }, [props.contentItem])

    const {data: currentFacebookPage} = useSWR(`get-facebook-page-${organizationStore.currentOrganization.id}`, async () => {
        const client = await WatsonApi();
        const response = await client.apis.organizations.organizations_facebook_token_retrieve_token({
            organization_pk: organizationStore.currentOrganization.id,
        })
        const result = JSON.parse(response.data);
        return result.page_name;
    }, {
        revalidateIfStale: false,
        revalidateOnMount: false,
    })

    useEffect(() => {
        if (sidebarStore.sidebar?.sbtype) {
            setContentItemType(sidebarStore.sidebar.sbtype as SupportedContentItemTypes);
        } else if (!contentItem) {
            setContentItemType(null);
        }
    }, [sidebarStore.sidebar, contentItem])

    const [title, setTitle] = useState<string>("");
    // text must begin as null so we can change it to string and MarkdownEditor will load in
    const [text, setText] = useState<string | null>(null);
    const [altText, setAltText] = useState<string>("");
    const [image, setImage] = useState<string>("");

    const defaultTimes = getDefaultTimes();
    const [allDay, setAllDay] = useState<boolean>(false);
    const [startDate, setStartDate] = useState<Moment>(defaultTimes.start);
    const [endDate, setEndDate] = useState<Moment>(defaultTimes.end);
    const [location, setLocation] = useState<string>("");
    const [selectedOrganizations, _setSelectedOrganizations] = useState<IMinimalOrganization[]>([organizationStore.currentOrganization]);
    const [organizationId, setOrganizationId] = useState<string | undefined>(props.contentItem?.organization_id || organizationStore.currentOrganization.id)

    const [shareRequestOrganizations, setShareRequestOrganizations] = useState<IMinimalOrganization[]>([]);

    const handleOrganizationsChange = async (orgs: IMinimalOrganization[]) => {
        const {
            selectedOrganizations: selectedOrgs,
            shareRequestOrganizations: requestOrgs,
            primaryOrganizationId: primaryOrgId,
        } = await validateContentItemSharingOrganizations(
            organizationId || organizationStore.currentOrganization.id,
            contentItem?.id,
            orgs.map(o => o.id)
        );

        setOrganizationId(primaryOrgId);
        _setSelectedOrganizations(selectedOrgs);
        setShareRequestOrganizations(requestOrgs);
    };

    const [showDateSelector, setShowDateSelector] = useState<boolean>(false);
    const [showLocationEditor, setShowLocationEditor] = useState<boolean>(false);
    const [showUrlEditor, setShowUrlEditor] = useState<boolean>(false);
    const [showLocaleSelector, setShowLocaleSelector] = useState<boolean>(false);

    const [file, setFile] = useState<null | IGoogleDriveFile>(null);

    const [url, setUrl] = useState<string>("");
    const [urlText, setUrlText] = useState<string>("");

    const [language, setLanguage] = useState<string>(i18nStore.orgDefaultLocale);
    const [discussionPrompt, setDiscussionPrompt] = useState<string>(contentItem?.discussion_prompt || "");
    const [isGeneratingPrompt, setIsGeneratingPrompt] = useState<boolean>(false);
    const [isPromptEditable, setIsPromptEditable] = useState<boolean>(!props.contentItem);

    useEffect(() => {
        if (contentItem) {
            setOrganizationId(contentItem.organization_id);
            setTitle(contentItem?.title || "");
            setText(contentItem?.description || "");
            setContentItemType(() => {
                return typeFromContentItem(contentItem);
            });
            setMeritChatEnabled(contentItem?.meritchat_enabled || false);
            setPublished(contentItem?.published);
            setLanguage(contentItem?.language);
            setAltText(() => {
                if (isNews(contentItem)) {
                    return contentItem.json_data.news.cover_image_alt_text || contentItem?.title || "";
                } else if (isMedia(contentItem) && contentItem.type.includes("photo")) {
                    return contentItem.json_data.images?.[0].alt_text || contentItem?.title || "";
                }
                return ""
            });
            setImage(() => {
                if (isNews(contentItem)) {
                    return contentItem.json_data.news.cover_image_url || "";
                } else if (isEvent(contentItem)) {
                    return contentItem.json_data.event.full_picture || "";
                } else if (isFile(contentItem)) {
                    return contentItem.json_data.file.cover_image_url || "";
                } else if (isMedia(contentItem) && contentItem.type.includes("photo")) {
                    // custom content items can't have videos so we don't need to handle those
                    return contentItem.json_data.images?.[0].raw_url || "";
                }
                return ""
            });

            setAllDay(isEvent(contentItem) ? contentItem.json_data.event.all_day : false);
            setStartDate(isEvent(contentItem) ? moment(contentItem.json_data.event.start) : defaultTimes.start);
            setEndDate(isEvent(contentItem) ? moment(contentItem.json_data.event.end) : defaultTimes.end);
            setLocation(isEvent(contentItem) ? contentItem.json_data.event.location : "");

            // @ts-ignore
            _setSelectedOrganizations(initializeSharingOrganizations(
                contentItem.organization,
                contentItem.share_organizations
            ));
            // @ts-ignore
            setShareRequestOrganizations(contentItem.share_request_organizations);
            setShowDateSelector(isEvent(contentItem) || false);
            setShowLocationEditor(isEvent(contentItem) && !!contentItem.json_data.event.location || false);

            setFile(isFile(contentItem) ?
                {
                    mimeType: contentItem.json_data.file.mime_type,
                    embedUrl: contentItem.json_data.file.view_link,
                    id: contentItem.third_party_id,
                } as IGoogleDriveFile : null);

            if (isNews(contentItem)) {
                setUrl(contentItem?.json_data.news?.topic_url || "");
                setUrlText(contentItem?.json_data.news?.topic_url_text || "");
                setDiscussionPrompt(contentItem?.discussion_prompt || "")
            }

            setIsPromptEditable(contentItem?.is_prompt_editable ?? true);

            send("FULFILL");
        }
    }, [contentItem])

    const [uploading, setUploading] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    async function handleSubmit() {
        let newItem: any = {};
        if (contentItem && !contentItem.is_custom) {
            newItem = {
                id: contentItem.id,
                organization_id: contentItem.organization_id,
                user_published: published,
            }
            if (contentItemType === SupportedContentItemTypes.TOPIC) {
                newItem.meritchat_enabled = meritChatEnabled;
                if (discussionPrompt) {
                    newItem.discussion_prompt = discussionPrompt;
                }
            }
        } else {
            newItem = {
                title,
                organization_id: organizationId,
                description: text,
                source_id: contentItem ? contentItem.source_id : -1,
                is_event: false,
                is_news: false,
                is_file: false,
                is_media: false,
                json_data: {},
                user_published: published,
                content_item_shares: formatContentItemSharesForBackend(
                    selectedOrganizations,
                    shareRequestOrganizations
                ),
                language,
            };

            if (contentItem) {
                newItem.id = contentItem.id;
                newItem.organization_id = contentItem.organization_id;
            }

            if (contentItemType === SupportedContentItemTypes.EVENT) {
                newItem.is_event = true;
                newItem.type = "custom.event";
                newItem.third_party_id = `custom_event_${Date.now()}`;
                newItem.created_at = startDate.format(submitDateFormat);
                newItem.json_data.event = {
                    end: endDate.format(submitDateFormat),
                    start: startDate.format(submitDateFormat),
                    location: showLocationEditor ? location : "",
                    multi_day: !endDate.isSame(startDate, 'day'),
                    full_picture: image ? image : "",
                    full_picture_alt_text: altText,
                    start_date_day: startDate.format('DD'),
                    start_day_month: startDate.format('MMM'),
                    end_date_day: endDate.format('DD'),
                    end_day_month: endDate.format('MMM'),
                    all_day: allDay,
                }
            } else if (contentItemType === SupportedContentItemTypes.FILE && file) {
                newItem.is_file = true;
                newItem.type = "drive.file";
                newItem.third_party_id = file.id;
                newItem.json_data.file = {
                    cover_image_url: image ? image : "",
                    cover_image_alt_text: altText,
                    view_link: file.url,
                    mime_type: file.mimeType,
                    updated_at: moment.utc(file.lastEditedUtc).format(submitDateFormat),
                }
            } else if (contentItemType === SupportedContentItemTypes.NEWS || contentItemType === SupportedContentItemTypes.TOPIC) {
                newItem.is_news = true;
                newItem.type = "custom.news";
                newItem.third_party_id = `custom_news_${Date.now()}`;
                newItem.json_data.news = {
                    cover_image_url: image ? image : "",
                    cover_image_alt_text: altText,
                    topic_url: showUrlEditor ? url : undefined,
                    topic_url_text: showUrlEditor ? urlText : undefined,
                }
                if (contentItemType === SupportedContentItemTypes.TOPIC) {
                    newItem.meritchat_enabled = true;
                }
                if (discussionPrompt) {
                    newItem.discussion_prompt = discussionPrompt;
                }
            } else if (contentItemType === SupportedContentItemTypes.MEDIA) {
                newItem.is_media = true;
                newItem.type = "custom.photo";
                newItem.third_party_id = `custom_image_${Date.now()}`;
                newItem.json_data.images = [{
                    raw_url: image ? image : "",
                    alt_text: altText,
                }]
            }
        }

        try {
            if (props.contentItem) {
                send("FETCH");
                const expand: string[] = []
                if (newItem.meritchat_enabled) {
                    expand.push("discussion_prompt");
                }
                const client = await WatsonApi();
                const result = await client.apis.organizations.organizations_content_items_partial_update({
                    organization_pk: newItem.organization_id,
                    id: newItem.id,
                    expand,
                    data: {
                        ...newItem,
                        organization_id: newItem.organization_id,
                    },
                });
                const updatedItem = JSON.parse(result.data);
                props.contentItem.update({...updatedItem});
            } else {
                send("FETCH");
                if (sidebarStore.sidebar.handleSave) {
                    sidebarStore.sidebar.handleSave(newItem, selectedOrganizations[0], postToFacebook);
                } else {
                    await handleSave(newItem, selectedOrganizations[0], postToFacebook);
                }
            }

            NotificationManager.success(`Item ${contentItem ? "updated" : "created"} successfully.`)
            sidebarStore.close();
            send("FULFILL");
        } catch (e) {
            console.log(e);
            send("REJECT");
            NotificationManager.error("Whoops, something went wrong!");
        }
    }

    async function handleDelete(contentItem) {
        try {
            send("FETCH");
            await sidebarStore.sidebar.handleDelete(contentItem);
            sidebarStore.close();
            NotificationManager.success("Item deleted.")
            send("FULFILL");
        } catch (e) {
            send("REJECT");
            NotificationManager.error("Whoops, something went wrong!");
        }
    }

    async function handleGeneratePrompt(){
        try {
            setIsGeneratingPrompt(true);
            const client = await WatsonApi();
            const result = await client.apis.organizations.organizations_content_items_generate_discussion_prompt({
                organization_pk: organizationStore.currentOrganization.id,
                id: null,
                data: {
                    title: title,
                    description: text,
                },
            })
            if (result && result.obj && result.obj.prompt) {
                setDiscussionPrompt(result.obj.prompt);
                NotificationManager.success("Prompt generated.")
                setIsGeneratingPrompt(false);
            }
            else {
                setIsGeneratingPrompt(false);
                NotificationManager.error("Whoops, something went wrong!");
                console.error(result)
            }
        }
        catch (e) {
            console.error(e)
            setIsGeneratingPrompt(false);
            NotificationManager.error("Whoops, something went wrong!");
        }
    }

    let media;
    if (image) {
        media = <>
            <ImageComponent src={getCdnUrl(image)}/>
            <button onClick={() => {
                setImage("");
            }}><FontAwesome name={'fa-times'} prefix={'fas'}/></button>
        </>
    } else if (contentItemType === SupportedContentItemTypes.EVENT && startDate) {
        media = <div className={listItemStyles.dateMedia}>
            <span>{startDate.format("MMM")}</span>
            <span>{startDate.format("D")}</span>
        </div>
    }

    const contentContainer = classNames({
        [styles.imageContainer]: true,
        [styles.error]: uploadError,
        [listItemStyles.groupContainer]: image,
    })

    const messageContainer = classNames({
        [styles.required]: postToFacebook && !text,
        [styles.whiteContent]: true,
    })

    const disableSubmitButton = uploading ||
        current.value === "PENDING" ||
        !title ||
        (contentItemType === SupportedContentItemTypes.FILE && !file) ||
        (contentItemType === SupportedContentItemTypes.MEDIA && !image) ||
        (image && !altText) ||
        (url && !urlText) ||
        (postToFacebook && !text) ||
        selectedOrganizations.length === 0 ||
        !organizationId;

    const submitButtonClassName = classNames({
        [styles.submitButton]: true,
        [styles.submitButtonDisabled]: disableSubmitButton,
    })

    const editContentClassName = classNames({
        [styles.cantEditContent]: contentItem && !contentItem?.is_custom,
    })

    if (!props.contentItem && !contentItemType) {
        return <ContentItemSelect setContentItemType={async type => {
            await sidebarStore.setSidebar({
                ...sidebarStore.sidebar,
                sbtype: type,
            })
        }}/>
    }

    function handleOpenSourcePage() {
        sidebarStore.setSidebar({view: 'EditSource'})
        sidebarStore.setQueriesToOnly(["spview", "EditSource"], ["id", contentItem?.source_id])
    }


    return <>
        {current.value === "PENDING" ?
            <FontAwesome className={styles.loadingSpinner} prefix={"fas"} name={"fa-spinner"} spin size={'2x'}/>
            :
            <>
                    {(contentItem && !contentItem?.organization?.is_editor) ?
                    <div
                        className={styles.noPermissionText}>{`This content item is owned by ${contentItem?.organization?.title}, please contact the administrators of that 
                page if you would like to make changes to this item.`}</div>
                        :
                        <div className={`${styles.postContainer} notranslate`}>
                        <div>
                                {contentItem && !contentItem?.is_custom &&
                                <div className={styles.alertMessage}>
                                        <h3>Alert: This content item was imported and is therefore uneditable.</h3>
                                        <hr/>
                                        {contentItem && (contentItem.is_file || contentItem.is_folder) && contentItem.type.includes("drive") &&
                                        <div className={styles.descriptionEditingInstructions}>
                                                <h4>To make edits to the Content Item:</h4>
                                                <ol>
                                                <li>Locate the {contentItem.is_file ? "file" : "folder"} in Google
                                                        Drive
                                                    </li>
                                                <li>To add a description
                                                        <ol>
                                                        <li>Right click the {contentItem.is_file ? "file" : "folder"} in
                                                                Google Drive.
                                                            </li>
                                                        <li>Select File Information {'>'} Details.</li>
                                                        <li>Scroll down on the right side information panel to add/edit
                                                                a 'Description'.
                                                            </li>
                                                    </ol>
                                                    </li>
                                            </ol>
                                                <span>The description should be updated after the source refreshes which you can manually do from our <button
                                                onClick={handleOpenSourcePage}>Content Source Manager</button></span>
                                            </div>
                                    }
                                    </div>
                            }
                                {<div className={editContentClassName}
                                  title={contentItem && !contentItem?.is_custom ? 'Non-custom content items cannot be edited' : undefined}>
                                <div>
                                        {contentItemType &&
                                        gridType === GridTypes.terraced && interfaceStore.view === "default" &&
                                        values(gridStore.blocks).findIndex(t => t.type === contentItemType) === -1 &&
                                            <div style={{marginBottom: '10px'}}>
                                                Item will not be visible on page
                                                unless {terraceTypeToTitleMap[getTerraceTypeFromSupportedContentItemType(contentItemType)]} Terrace
                                                is enabled!
                                            </div>}
                                        {uploadError && <div className={styles.error}>
                                        <p>{uploadError}</p>
                                    </div>}
                                        <div className={contentContainer}>
                                        {media}
                                        <div>
                                                <RequiredInput required={true} valid={!!title}
                                                           placeholder={"Add a Title (required)"} value={title}
                                                           onChange={e => setTitle(e.target.value)}/>
                                                {image && <RequiredInput required={true} valid={!!altText}
                                                                     placeholder={"Image Alt Text (required)"}
                                                                     value={altText}
                                                                     onChange={e => setAltText(e.target.value)}/>}
                                                {(intent === "create" || typeof text === "string") && <MarkdownEditor editorClassName={messageContainer} value={text || ""}
                                                            handleTextChange={setText}
                                                            placeholder={"What would you like to say?"}/>}
                                                {contentItemType === SupportedContentItemTypes.FILE &&
                                                <p>If you would like to add a folder rather than an individual file, you
                                                        can add it as a Content Source in our <button
                                                        style={{color: '#1700a1', whiteSpace: 'nowrap'}}
                                                        onClick={() => {
                                                            sidebarStore.setSidebar({
                                                                sbtype: 'GOOGLE_DRIVE',
                                                                view: 'AddSocial',
                                                            })
                                                        }}>Content Source manager</button></p>}
                                            </div>

                                    </div>
                                        {showUrlEditor && <UrlEditor
                                        url={url}
                                        setUrl={setUrl}
                                        text={urlText}
                                        setText={setUrlText}
                                    />}
                                        {showDateSelector && <DateEditor
                                        allDay={allDay}
                                        setAllDay={setAllDay}
                                        startDate={startDate}
                                        setStartDate={setStartDate}
                                        endDate={endDate}
                                        setEndDate={setEndDate}
                                />
                                }
                                        {showLocationEditor && <LocationEditor setLocation={setLocation} location={location}/>}
                                        {showLocaleSelector && <OrgSupportedLocaleSelector
                                    disabled={!(contentItem?.is_custom || !contentItem)}
                                    value={localeRegistry[language]._englishName}
                                    handleSelectorChange={setLanguage}
                                    className={styles.localeSelector}
                                />}
                                {file &&
                                        <div className={styles.fileUrl}>
                                                {<ImageComponent
                                        src={getIconByMimeType("google", file.mimeType)}/>}<span>{file.name}
                                            <button onClick={() => {
                                        setFile(null);
                                            }}>
                                                <FontAwesome prefix={'fas'} name={'fa-times'}/>
                                            </button></span>

                                            </div>
                                }

                                        <div className={styles.buttons}>
                                        <ImageEditor
                                        image={image}
                                        setImage={setImage}
                                        file={file}
                                        uploading={uploading}
                                        setUploading={setUploading}
                                        setUploadError={setUploadError}
                                        contentItemType={contentItemType}
                                    />
                                        <RequiredDiv required={false} valid={true}>
                                        <button
                                            title={"Locale"}
                                            onClick={() => setShowLocaleSelector(v => !v)}
                                        >
                                            <FontAwesome prefix={"fas"} name={"fa-language"} />
                                            Locale
                                        </button>
                                    </RequiredDiv>{(contentItemType === SupportedContentItemTypes.NEWS || contentItemType === SupportedContentItemTypes.TOPIC) &&
                                                <RequiredDiv required={false} valid={true}>
                                                <button
                                                title={"Add a link"}
                                                onClick={() => setShowUrlEditor(v => !v)}
                                            >
                                                        <FlatIcon name={'flaticon-link'}/>
                                                        Link
                                                    </button>
                                            </RequiredDiv>}
                                        {contentItemType === SupportedContentItemTypes.EVENT &&
                                                <RequiredDiv required={false} valid={true}>
                                                <button
                                                title={"Add a location"}
                                                onClick={() => setShowLocationEditor(v => !v)}
                                            >
                                                        <FlatIcon name={'flaticon-location'}/>
                                                        Location
                                                    </button>
                                            </RequiredDiv>}
                                        {contentItemType === SupportedContentItemTypes.EVENT &&
                                                <RequiredDiv required={false} valid={true}>
                                                <button
                                                title={"Add a date"}
                                                onClick={() => {
                                                    setShowDateSelector(v => !v)
                                                }}>
                                                        <FlatIcon name={'flaticon-event'}/>
                                                        Date
                                                    </button>
                                            </RequiredDiv>}
                                        {contentItemType === SupportedContentItemTypes.FILE && <RequiredDiv valid={!!file}>
                                                <button
                                            title={"Add a file"}
                                            onClick={() => {
                                                modalStore.addModal({
                                                    type: "googleFilePicker",
                                                    handlePicked: data => {
                                                        setFile(data);
                                                    },
                                                    role: "sender",
                                                    viewIds: ["DOCS"],
                                                    src: publicRuntimeConfig.API_PHP_URL + "/partials/googleFilePicker",
                                                })
                                            }}>
                                                <FlatIcon name={'flaticon-drive'}/>
                                                File
                                                </button>
                                            </RequiredDiv>}
                                    </div>

                                    </div>
                            </div>}
                                {(contentItem?.is_custom || !contentItem) && <SharePathsEditor
                                key={contentItem? contentItem.id : 1}
                                selectedOrganizations={selectedOrganizations}
                                shareRequestOrganizations={shareRequestOrganizations}
                                onOrganizationsChange={handleOrganizationsChange}
                                allowSelectNonEditorOrganizations={true}
                            />}
                                {(contentItemType === SupportedContentItemTypes.NEWS || contentItemType === SupportedContentItemTypes.TOPIC || contentItemType === SupportedContentItemTypes.MEDIA) &&
                                <div>
                                        {contentItem ?
                                        <>
                                                {contentItem?.is_custom &&
                                                <p>Editing content on SchoolBlocks will not affect content posted to
                                                        third party sites.</p>}
                                            </> :
                                            <div className={styles.newsPostOption}>
                                            <ToggleSwitch
                                                enabled={currentFacebookPage}
                                                on={postToFacebook}
                                                onClick={() => setPostToFacebook(!postToFacebook)}
                                            />
                                            <button title={"Manage Social Media Integration"}
                                                        onClick={() => modalStore.addModal({
                                                        type: "notificationChannels",
                                                    })}>
                                                    <label>{currentFacebookPage ? "Post" : "Connect"} to
                                                    Facebook</label>
                                                </button>
                                        </div>}
                                        {contentItemType === SupportedContentItemTypes.TOPIC && <div className={styles.newsPostOptionVertical}>
                                            <textarea
                                                id={"discussion-prompt-textarea"}
                                                value={discussionPrompt}
                                                onChange={e => setDiscussionPrompt(e.target.value)}
                                                placeholder={"Add a question or discussion prompt."}
                                                disabled={isGeneratingPrompt || !isPromptEditable}/>
                                        {isPromptEditable ?
                                            <div className={styles.tooltip}
                                                 data-content={!title ? "AI prompt requires a title":""}>
                                                <Button
                                                    disabled={!title || isGeneratingPrompt}
                                                    onClick={handleGeneratePrompt}
                                                    className="btn sb-organization-color-element-bg">
                                                    {isGeneratingPrompt ? (
                                                        <><FontAwesome name={"fas fa-spinner"} spin/>&nbsp;Generating...</>
                                                    ) : (
                                                        <><FontAwesome name={"fas fa-wand-magic-sparkles"}/>&nbsp;Suggest a prompt</>
                                                    )}
                                                </Button>
                                            </div> :
                                            <div className={styles.tooltip}>
                                                <FontAwesome name={"fas fa-info-circle"}/>&nbsp;The prompt is no longer editable as the discussion has started.
                                            </div>
                                        }


                                        </div>}
                                    </div>
                            }
                                {contentItemType && <div className={styles.newsPostOption}>
                                <ToggleSwitch
                                    enabled={true}
                                    on={published}
                                    onClick={() => setPublished(v => !v)}
                                />
                                <label>Published<span
                                    className={styles.tooltip}
                                    data-content={"Toggle this content item's visibility"}
                                    style={{marginLeft: ".25rem"}}
                                ><FontAwesome prefix={'fas'} name={'fa-question-circle'}/></span></label>
                            </div>}
                            </div>
                        <div className={styles.submitButtonContainer}>
                                <button className={submitButtonClassName}
                                    onClick={handleSubmit}>{contentItem ? "Save" : "Post"}</button>
                                {contentItem && contentItem.is_custom && <button className={styles.submitButton}
                                                                             onClick={() => handleDelete(contentItem)}>Delete</button>}
                            </div>
                    </div>}
                </>
        }
    </>
});
